/**
 * What a framed Chat42 does with a session id its host stored and sent back.
 *
 * Three answers, not two. The session is there, and the frame opens it. The server says there
 * is no such session for this person, and the frame makes one, because the host's stored id has
 * outlived what it named. Or the question could not be answered (a 500, a refused token, a
 * connection that dropped or timed out), and the frame makes nothing, tells the host, and offers
 * to ask again.
 *
 * The third used to be folded into the second. Every failed lookup read as "gone", so a brief
 * outage replaced a host's valid session with a new, empty one, and the host stored the new id
 * over the old. The conversation was still on the server, but nothing pointed to it any more.
 * Only the server saying so is "gone": `findSession` answers undefined for a 404 and throws for
 * everything else.
 *
 * A pure decision, kept out of the app so it can be tested without a frame: the app's `main.tsx`
 * renders as it is imported.
 */

/** What reopening needs of a client. `Chat42Client` is one; a test's fake is another. */
export interface StoredSessionClient<S> {
  findSession(sessionId: string): Promise<S | undefined>;
}

export type StoredSession<S> =
  /** Open it. */
  | { kind: 'found'; session: S }
  /** The server says there is no such session for this person: make one. */
  | { kind: 'absent' }
  /** Nobody knows whether it is there. Make nothing; say this, and offer to try again. */
  | { kind: 'failed'; message: string };

export async function reopenStoredSession<S>(
  client: StoredSessionClient<S>,
  sessionId: string,
): Promise<StoredSession<S>> {
  try {
    const session = await client.findSession(sessionId);
    return session === undefined ? { kind: 'absent' } : { kind: 'found', session };
  } catch (err) {
    return { kind: 'failed', message: couldNotReopen(err) };
  }
}

/** The failure in plain words, ending with what was not done, because that is the reassurance. */
function couldNotReopen(err: unknown): string {
  const status = typeof (err as { status?: unknown })?.status === 'number'
    ? (err as { status: number }).status
    : undefined;
  const why = status === 401 || status === 403
    ? 'the sign-in it was given was refused'
    : status !== undefined
      ? `Chat42 answered with an error (${status})`
      : 'Chat42 could not be reached';
  return `Could not reopen this conversation: ${why}. Nothing has been replaced. Try again.`;
}
